/* eslint react-hooks/exhaustive-deps: 0 */

import { Card, message, TablePaginationConfig } from "antd";
import { useEffect, useState, useRef } from "react";
import { deleteDarkIcon } from "../../../assets/icons";
import { ModalButton, ModalPrimaryText, ModalType } from "../../../config/enum";
import { UIConfirmModal } from "../../common/confirmModal";
import { UIDropdown } from "../../common/dropdown";
import * as util from "util";
import { UITable } from "../../common/table"
import './style.css'
import { FilterValue, SorterResult } from "antd/lib/table/interface";
import { page, rolesOrder } from "../../../config/constants";
import { removeCurrentStep, removeProcessState, setProcessState, setSyncedData, selectCurrentAppState } from "../../../redux/features/app/appSlice";
import { useGetAllActiveProcessQuery, useStopActiveProcessMutation } from "../../../redux/services/processSlice";
import { useDispatch, useSelector } from "react-redux";
import { authStateSelector } from "../../../redux/features/auth/authSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { Page } from "../../../routes/config";
import UITooltip from "../../common/tooltip";
import { AuditOutlined, EllipsisOutlined } from "@ant-design/icons";
import { getFilterInfo, setFilterState } from "../../../redux/features/filter/filterSlice";

export const ActiveProcess = (props:any) => {
  const [tableData, setTableActiveData] = useState<any[]>([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>({});
  const [total, setTotal] = useState(0);
  const filterState = useSelector(getFilterInfo);
  const currentURL = window.location.href;
  const location = useLocation();
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: filterState?.page &&  currentURL === filterState?.url ?  filterState?.page : page?.current,
    pageSize: filterState.limit && currentURL === filterState?.url ? filterState.limit : page?.pageSize,
    showSizeChanger: true,
  });
  const {tenantId, user} = useSelector(authStateSelector)
  const appState = useSelector(selectCurrentAppState);
  const processState = appState?.processState || {};
  const dispatch = useDispatch();
  const lastRefetchedPathname = useRef<string | null>(null);
  const [sort, setSort] = useState<any>({
    ...((currentURL === filterState?.url && filterState?.sortBy) && {sortBy: filterState?.sortBy}),
    ...((currentURL === filterState?.url && filterState?.sortOrder) && {sortOrder: filterState?.sortOrder}),
  });
  let queryParams = {
    page:  pagination.current,
    limit: pagination.pageSize,
    type: "ActiveProcess",
    tenantId: tenantId,
    ...sort
  }

  useEffect(()=>{
    if (dispatch) {
      dispatch(setFilterState({
        page: queryParams?.page,
        limit : queryParams?.limit,
        sortBy: queryParams?.sortBy,
        sortOrder: queryParams?.sortOrder
      }));
    }
},[
  queryParams?.page,
  queryParams?.limit,
  queryParams?.sortBy,
  queryParams?.sortOrder
]);

  // Add polling to keep active process list fresh (every 5 seconds)
  const getActiveProcess = useGetAllActiveProcessQuery(
    {queryParams, roleId: user?.roleId}, 
    {
      skip: !user?.roleId || !tenantId, 
      refetchOnFocus: true, 
      refetchOnReconnect: true, 
      pollingInterval: 5000 
    }
  )

  // Use query's loading state directly, but also handle cached data
  const loading = getActiveProcess?.isLoading || getActiveProcess?.isFetching || false;

  // Set data whenever it's available (from cache or fresh fetch)
  // Always ensure processes in READY state are displayed
  // CRITICAL: Use ref to track previous data to prevent losing processes from other sites/zones
  // when refetch is called from activity logs page
  const previousProcessesRef = useRef<any[]>([]);
  
  useEffect(()=>{
    // Check if we're on the active process page
    const isOnActiveProcessPage = location.pathname === Page.ACTIVE_PROCESS || location.pathname === '/';
    
    if(getActiveProcess?.data?.result !== undefined){
      const processes = getActiveProcess?.data?.result || [];
      const previousProcesses = previousProcessesRef.current;
      const isQueryFetching = getActiveProcess?.isFetching || getActiveProcess?.isLoading;
      
      // CRITICAL: Prevent processes from disappearing when refetch is called from another page
      // Only update table data if:
      // 1. Query is not currently fetching (to avoid race conditions with incomplete data)
      // 2. AND (we're on the active process page OR new data has more/equal processes)
      // This prevents processes from other sites/zones from disappearing when activity logs page calls refetch
      const shouldUpdate = !isQueryFetching && (isOnActiveProcessPage || processes.length >= previousProcesses.length);
      
      if (shouldUpdate) {
        // Setting table data - processes received (confirmed update)
        setTableActiveData(processes);
        setTotal(processes.length);
        previousProcessesRef.current = processes;
        
        const activeProcessKeys = new Set(
          processes.map((p: any) => `${p.processId} ${p.deviceId}`)
        );
        
        // Get all process states from Redux
        const allProcessStates = Object.keys(processState || {});
        
        // Remove states for processes that are no longer active
        // This aligns with backend cleanup - if backend removed it, frontend should too
        allProcessStates.forEach((key: string) => {
          if (!activeProcessKeys.has(key)) {
            const [processId, deviceId] = key.split(' ');
            // Cleaning up orphaned Redux state
            dispatch(removeProcessState({ processId, deviceId }));
            dispatch(removeCurrentStep({ processId, deviceId }));
          }
        });
      } else {

        if (isQueryFetching) {
          console.log('[Active Process] Query is fetching - preserving existing data to prevent race conditions', {
            currentProcessCount: previousProcesses.length,
            newProcessCount: processes.length,
            isFetching: isQueryFetching,
            isOnActiveProcessPage
          });
        } else if (!isOnActiveProcessPage && processes.length < previousProcesses.length) {
          console.log('[Active Process] Refetch from another page with fewer processes - preserving existing data', {
            currentProcessCount: previousProcesses.length,
            newProcessCount: processes.length,
            location: location.pathname,
            note: 'This prevents processes from other sites/zones from disappearing when activity logs page calls refetch'
          });
        }
        // Keep previous data - don't update table
      }
    } else {
      if (isOnActiveProcessPage && !getActiveProcess?.isLoading && !getActiveProcess?.isFetching) {
        setTableActiveData([]);
        setTotal(0);
        previousProcessesRef.current = [];
      }
    }
  },[getActiveProcess?.data?.result, getActiveProcess?.isLoading, getActiveProcess?.isFetching, getActiveProcess?.refetch, processState, dispatch, location.pathname])

  useEffect(()=>{
    
    const isOnActiveProcessPage = location.pathname === Page.ACTIVE_PROCESS || location.pathname === '/';
    
    if (isOnActiveProcessPage && user?.roleId && tenantId) {
      // Only refetch if this is a new navigation (pathname changed)
      if (lastRefetchedPathname.current !== location.pathname) {
        // Navigating to active process page - refetching data
        
        // Update the ref to track this pathname
        lastRefetchedPathname.current = location.pathname;
        
        // Refetch when navigating to this page
        if (getActiveProcess?.refetch) {
          // Triggering refetch after navigation
          // Use a small delay to ensure navigation is complete
          const timeoutId = setTimeout(() => {
            if (getActiveProcess?.refetch) {
              getActiveProcess.refetch();
              // Refetch triggered
            }
          }, 100);
          
          return () => clearTimeout(timeoutId);
        }
      }
    } else {
      // Reset the ref when navigating away from the active process page
     
      if (lastRefetchedPathname.current !== null) {
        lastRefetchedPathname.current = null;
      }
    }
  },[location.pathname, user?.roleId, tenantId])

 
  const navigate = useNavigate()
  const [stopProcess, { isSuccess: isStopSuccess, isError: isStopError, error: stopError, reset: resetStopState }] =
    useStopActiveProcessMutation()

  // Handle successful stop exactly once per mutation call, then reset RTK Query mutation state.
  useEffect(() => {
    if (!isStopSuccess) {
      return
    }

    getActiveProcess?.refetch()
    dispatch(setSyncedData({}))
    dispatch(
      removeProcessState({
        processId: selectedRow?.processId,
        deviceId: selectedRow?.deviceId,
      })
    )
    dispatch(
      removeCurrentStep({
        processId: selectedRow?.processId,
        deviceId: selectedRow?.deviceId,
      })
    )
    message.success({
      content: "Process Stopped Successfully",
      key: "notificationKey",
      duration: 2,
      className: "successMessage",
    })

    if (typeof resetStopState === "function") {
      resetStopState()
    }
  }, [isStopSuccess, resetStopState, getActiveProcess?.refetch, dispatch, selectedRow?.processId, selectedRow?.deviceId])

  useEffect(() => {
    if (!isStopError) {
      return
    }
    const errorStatus = stopError?.status || stopError?.originalStatus || stopError?.data?.statusCode
    const errorMessage =
      stopError?.data?.error?.message ||
      stopError?.data?.message ||
      stopError?.message ||
      "Failed to stop process"

    console.error('[ActiveProcess] ========== Process Stop Failed ==========', {
      timestamp: new Date().toISOString(),
      errorStatus: errorStatus,
      errorMessage: errorMessage,
      process: {
        processId: selectedRow?.processId,
        processName: selectedRow?.processName,
        deviceId: selectedRow?.deviceId,
      },
      stoppedBy: {
        userId: user?.userId,
        username: user?.username,
        roleName: user?.roleName,
      },
      error: stopError,
      note: 'Failed to stop process - showing error message to user',
    })

    message.error({
      content: errorMessage,
      key: "notificationKey",
      duration: 2,
      className: "errorMessage",
    })

    if (typeof resetStopState === "function") {
      resetStopState()
    }
  }, [
    isStopError,
    stopError,
    resetStopState,
    selectedRow?.processId,
    selectedRow?.processName,
    selectedRow?.deviceId,
    user?.userId,
    user?.username,
    user?.roleName,
  ])

  /** Backend may return start/resume/running/Active-Running/active-running for a live run — treat all as "running" for UI. */
  const isRunningProcessingValue = (val: unknown): boolean => {
    if (typeof val !== "string") return false;
    const n = val.trim().toLowerCase().replace(/_/g, "-");
    return n === "start" || n === "resume" || n === "running" || n === "active-running";
  };

  const getActiveState = (row:any) => {
    let state = ""
    if(row?.isReadyForProcessing === "ready"){
      state = "start"
    } else if (isRunningProcessingValue(row?.isReadyForProcessing)) {
      state = "pause"
    } else if (row?.isReadyForProcessing === "pause") {
      state = "resume"
    }  else if (row?.isReadyForProcessing === "pause-processing" || row?.isReadyForProcessing === "pause_processing" || row?.isReadyForProcessing === "PAUSE_PROCESSING") {
      // Treat pause-processing as pause state for button display
      state = "resume" // Show resume button when in pause-processing state
    }

    return state
  }
  const onRowClick = (row: any) => {
    console.log('========================================');
    console.log('[ActiveProcess] onRowClick called');
    console.log('========================================');
    console.log('[ActiveProcess] Row data:', {
      processId: row?.processId,
      processName: row?.processName,
      userName: row?.userName,
      userId: row?.userId,
      roleName: row?.roleName,
      roleId: row?.roleId,
      deviceId: row?.deviceId,
      siteId: row?.siteId,
      zoneId: row?.zoneId,
      tenantId: row?.tenantId
    });
    console.log('[ActiveProcess] Current user data:', {
      userId: user?.userId,
      username: user?.username,
      roleName: user?.roleName,
      roleId: user?.roleId,
      tenantId: user?.tenantId
    });
    
    // Check if current user can view this process
    // Platform Admin can view all processes, others can only view their own
    // Use userId as fallback if userName/username is not yet populated (e.g., when process is just started)
    const isPlatformAdmin = user?.roleName === "Platform Admin";
    // Check username match - try row.username first, then row.userName (case-insensitive)
    const rowUsername = row?.username || row?.userName;
    const isSameUserName = rowUsername && user?.username && 
      rowUsername.toLowerCase() === user.username.toLowerCase();
    const isSameUserId = (!rowUsername || !isSameUserName) && user?.userId === row?.userId;
    
    console.log('[ActiveProcess] Access check results:', {
      isPlatformAdmin,
      isSameUserName,
      isSameUserId,
      userNameMatch: `${user?.username} === ${row?.userName} = ${isSameUserName}`,
      userIdMatch: `${user?.userId} === ${row?.userId} = ${isSameUserId}`
    });
    
    const canView = isPlatformAdmin || isSameUserName || isSameUserId;
    
    console.log('[ActiveProcess] Final canView result:', canView);
    
    if (canView) {
      console.log('[ActiveProcess]  Access GRANTED - Navigating to Activity Logs');
      //  User can view this process
      navigate(Page.ACTIVITY_LOGS, {
        state: {
          data: row,
          stopButton: true,
          startLoop: row?.processData?.isCustomizedLoop ? row?.processData?.startLoop : 0,
          isActiveProcess: true
        }
      })
    } else {
      console.log('[ActiveProcess]  Access DENIED - Showing error message');
      console.log('[ActiveProcess] Reason: User cannot view processes started by other users');
      // User cannot view processes started by other users
      message.info({
        content: "You can not view the process started by other users!",
        key: "notificationKey",
        duration: 2,
        className: "infoMessage"
      })
    }
    
    dispatch(setProcessState({
      processId: row?.processId,
      deviceId: row?.deviceId,
      state: getActiveState(row),
    }))
    setSelectedRow(row);
    console.log('========================================');
  };

  const dropdown = (row:any) => {
    console.log('========================================');
    console.log('[ActiveProcess] dropdown function called');
    console.log('========================================');
    console.log('[ActiveProcess] Row data:', {
      processId: row?.processId,
      processName: row?.processName,
      userName: row?.userName,
      userId: row?.userId,
      roleName: row?.roleName,
      roleId: row?.roleId,
      deviceId: row?.deviceId
    });
    console.log('[ActiveProcess] Current user data:', {
      userId: user?.userId,
      username: user?.username,
      roleName: user?.roleName,
      roleId: user?.roleId
    });
    
    const items: any[] = [];
    let canStop = false;
    let canViewActivityLogs = false;
    let canViewAuditLogs = false;
    
    // Helper function to check if same user (with fallback to userId)
    const isSameUser = () => {
      
      const rowUsername = row?.username || row?.userName;
      if (rowUsername && user?.username) {
        const usernameMatch = rowUsername.toLowerCase() === user.username.toLowerCase();
        console.log('[ActiveProcess] isSameUser (by username/userName):', {
          rowUsername: rowUsername,
          rowUserName: row?.userName,
          rowUsernameField: row?.username,
          userUsername: user.username,
          match: usernameMatch
        });
        if (usernameMatch) {
          return true;
        }
      }
      if (row?.userId && user?.userId) {
        const userIdMatch = row.userId === user.userId;
        console.log('[ActiveProcess] isSameUser (by userId fallback):', {
          rowUserId: row.userId,
          userUserId: user.userId,
          match: userIdMatch
        });
        if (userIdMatch) {
          return true;
        }
      }
      console.log('[ActiveProcess] isSameUser: No match found (both checks failed)');
      return false;
    };
    
    // Helper function to get effective roleName (with fallback from userId)
    const getEffectiveRoleName = () => {
      if (row?.roleName) {
        console.log('[ActiveProcess] getEffectiveRoleName: Using row.roleName =', row.roleName);
        return row.roleName;
      }
      if (row?.userId && user?.userId && row.userId === user.userId && user?.roleName) {
        console.log('[ActiveProcess] getEffectiveRoleName: Mapping from userId - using user.roleName =', user.roleName);
        return user.roleName;
      }
      console.log('[ActiveProcess] getEffectiveRoleName: No roleName available');
      return null;
    };
    
    // Helper function to get effective roleId (with fallback from userId)
    const getEffectiveRoleId = () => {
      if (row?.roleId) {
        const rowRoleIds = Array.isArray(row.roleId) ? row.roleId : [row.roleId];
        console.log('[ActiveProcess] getEffectiveRoleId: Using row.roleId =', rowRoleIds);
        return rowRoleIds;
      }
      if (row?.userId && user?.userId && row.userId === user.userId && user?.roleId) {
        const userRoleIds = Array.isArray(user.roleId) ? user.roleId : [user.roleId];
        console.log('[ActiveProcess] getEffectiveRoleId: Mapping from userId - using user.roleId =', userRoleIds);
        return userRoleIds;
      }
      console.log('[ActiveProcess] getEffectiveRoleId: No roleId available');
      return null;
    };
    
    // Helper function to check if same role (with fallback to roleId and userId mapping)
    const isSameRole = () => {
      const effectiveRoleName = getEffectiveRoleName();
      const effectiveRoleId = getEffectiveRoleId();
      
      // First try roleName comparison
      if (effectiveRoleName && user?.roleName) {
        const result = effectiveRoleName === user.roleName;
        console.log('[ActiveProcess] isSameRole (by roleName):', {
          effectiveRoleName,
          userRoleName: user.roleName,
          match: result
        });
        return result;
      }
      // Fallback: check roleId if roleName is not available
      if (effectiveRoleId && user?.roleId) {
        const result = effectiveRoleId.includes(user.roleId);
        console.log('[ActiveProcess] isSameRole (by roleId fallback):', {
          effectiveRoleId,
          userRoleId: user.roleId,
          match: result
        });
        return result;
      }
      console.log('[ActiveProcess] isSameRole: No match found (both checks failed)');
      return false;
    };
    
    // ============================================
    // CASE 1: Platform Admin - Full Access
    // ============================================
    if (user?.roleName === "Platform Admin") {
      console.log('[ActiveProcess] CASE 1: Platform Admin - Full Access');
      //  Platform Admin has full access to all processes
      canStop = true;
      canViewActivityLogs = true;
      canViewAuditLogs = true;
    }
    // CASE 2: Same Role as Process Starter
    else if (isSameRole()) {
      console.log('[ActiveProcess] CASE 2: Same Role detected');
      if (isSameUser()) {
        console.log('[ActiveProcess] CASE 2a: Same user, same role - Full Access');
        canStop = true;
        canViewActivityLogs = true;
        canViewAuditLogs = true;
      } else {
        console.log('[ActiveProcess] CASE 2b: Different user, same role - NO ACCESS');
        // Case 2b: Different user, same role
        //  No access - return null (no dropdown shown)
        console.log('[ActiveProcess]  Returning null - No dropdown shown');
        console.log('========================================');
        return null
      }
    } 
    // CASE 3: Different Role - Check Hierarchy
    else {
      console.log('[ActiveProcess] CASE 3: Different Role - Checking Hierarchy');
      
      // Get effective roleName (with fallback from userId mapping)
      const effectiveRoleName = getEffectiveRoleName();
      const currentUserRoleOrder = rolesOrder?.[user?.roleName];
      
     
      let processStarterRoleOrder = null;
      if (effectiveRoleName) {
        processStarterRoleOrder = rolesOrder?.[effectiveRoleName];
      }
      
      console.log('[ActiveProcess] Hierarchy check:', {
        rowRoleName: row?.roleName,
        effectiveRoleName,
        processStarterRoleOrder,
        userRoleName: user?.roleName,
        currentUserRoleOrder,
        rolesOrderAvailable: !!rolesOrder
      });
      
      // If roleName is missing, check if it's the same user (grant access) or deny
      if (!processStarterRoleOrder || !currentUserRoleOrder) {
        console.log('[ActiveProcess] CASE 3: Role order missing - Checking if same user');
        // If we can't determine hierarchy, check if it's the same user
        if (isSameUser()) {
          console.log('[ActiveProcess] CASE 3: Same user but role info missing - Granting access as fallback');
          canStop = true;
          canViewActivityLogs = true;
          canViewAuditLogs = true;
        } else {
          console.log('[ActiveProcess] CASE 3: Cannot determine access - DENY');
          // Can't determine access - deny
          console.log('[ActiveProcess] Returning null - No dropdown shown');
          console.log('========================================');
          return null;
        }
      } else if (processStarterRoleOrder > currentUserRoleOrder) {
        console.log('[ActiveProcess] CASE 3: Current user has HIGHER authority - Can stop only');
        //  Current user has HIGHER authority
        // Can stop the process (but not view or see audit logs)
        canStop = true;
        canViewActivityLogs = false;
        canViewAuditLogs = false;
      } else {
        console.log('[ActiveProcess] CASE 3: Current user has LOWER or EQUAL authority - NO ACCESS');
        //  Current user has LOWER or EQUAL authority
        // No access - return null (no dropdown shown)
        console.log('[ActiveProcess]  Returning null - No dropdown shown');
        console.log('========================================');
        return null
      }
    }
    
    console.log('[ActiveProcess] Final permissions:', {
      canStop,
      canViewActivityLogs,
      canViewAuditLogs
    });
    
    // Add menu items based on permissions
    if (canStop) {
      items.push({
        label: "Stop and Exit",
        key: "1",
        icon: <img src={deleteDarkIcon} alt="delete" />,
        onClick: () => onDeleteClick(row),
      });
      console.log('[ActiveProcess] Added "Stop and Exit" menu item');
    }
    
    if (canViewActivityLogs) {
      items.push({
        label: "View Activity Logs",
        key: "2",
        icon: <AuditOutlined />,
        onClick: () => onRowClick(row),
      });
      console.log('[ActiveProcess] Added "View Activity Logs" menu item');
    }
    
    if (canViewAuditLogs) {
      items.push({
        label: "View Audit Logs",
        key: "3",
        icon: <AuditOutlined />,
        onClick: () => navigate('/auditlogs', {state: row}),
      });
      console.log('[ActiveProcess] Added "View Audit Logs" menu item');
    }
    
    console.log('[ActiveProcess] Total menu items:', items.length);
    
    // Only show dropdown if user has at least one permission
    if (items.length > 0) {
      console.log('[ActiveProcess] Showing dropdown with', items.length, 'items');
      console.log('========================================');
      return (
        <UIDropdown
          data-testid="delete"
          items={items}
          placement="bottom"
        >
          <EllipsisOutlined className="ellipses" style={{ fontSize: '16px', cursor: 'pointer' }} />
        </UIDropdown>
      );
    } else {
      // No access - don't show dropdown
      console.log('[ActiveProcess]  No menu items - Returning null (no dropdown shown)');
      console.log('========================================');
      return null;
    }
  }

  const onDeleteClick = (row: any) => {
    setDeleteModal(true);
    setSelectedRow(row);
  };

  const handleDelete = () => {
    stopProcess({userId: selectedRow?.userId, stopBy: user?.userId, processName:selectedRow?.processName ,activity_logs: false, processId:selectedRow?.processId,  type:"exit", deviceId:selectedRow?.deviceId})
    setDeleteModal(false);
  };
  const onDeleteModalClose = () => {
    setDeleteModal(false);
  };


 const getStatus = (data:any, row?: any)=>{
      // Handle false/boolean values - check processStates if available
      if (data === false || data === 'false') {
        if (row?.processStates) {
          const processState = row.processStates;
          if (isRunningProcessingValue(processState)) {
            return 'Running';
          } else if (processState === 'pause' || processState === 'pause-processing' || processState === 'pause_processing' || processState === 'PAUSE_PROCESSING') {
            return 'Pause';
          } else {
            return 'Running'; 
          }
        }
        // Default to Running when isReadyForProcessing is false and no processStates
        return 'Running';
      }
      
      if (isRunningProcessingValue(data)) {
        return 'Running'
      } else if(data === 'pause-processing' || data === 'pause_processing' || data === 'PAUSE_PROCESSING'){
        return 'Pause'
      } else if(data === 'ready'){
        return 'Ready'
      } else if(data === 'pause'){
        return 'Pause'
      } else {
        return String(data)
      }
  }

  const getColorDot = (data:any, row?: any) => {
    if (data === false || data === 'false') {
      if (row?.processStates) {
        const processState = row.processStates;
        if (isRunningProcessingValue(processState)) {
          return 'dot-green';
        } else if (processState === 'pause' || processState === 'pause-processing' || processState === 'pause_processing' || processState === 'PAUSE_PROCESSING') {
          return 'dot-red';
        }
      }
      return 'dot-green';
    }
    
    if (isRunningProcessingValue(data)) {
      return 'dot-green'
    }else if(data === 'pause' || data === 'pause-processing' || data === 'pause_processing' || data === 'PAUSE_PROCESSING'){
      return 'dot-red'
    }else if(data === 'ready'){
      return 'dot-yellow' 
    }else{
      return 'dot-yellow'
    }
  }

  const columns = [
    {
      title: "Tenant",
      dataIndex: "tenantName",
      className:"activeProcessTable",
      key:"tenantName",
      sorter: true,
      ellipsis: true,
      width: "30%",
      render: (field: any, row: any) => {
        return (
          <div
            className="d-flex align-items-center"
            onClick={() => onRowClick(row)}
          >
            <UITooltip title={row?.tenantName} placement="top">
            <div className="pl-10 tableData" key="tenantName">{field}</div>
            </UITooltip>
          </div>
        );
      },
    },
    {
      title: "Process Name",
      dataIndex: "processName",
      // sorter: true,
      key:"processName",
      className:"activeProcessTable",
      ellipsis: true,
      width: "30%",
      render: (field: any, row: any) => {
        return (
          <div
            className="d-flex align-items-center"
            onClick={() => onRowClick(row)}
          >
            <UITooltip title={row?.processName} placement="top">
            <div className="pl-10 tableData slice" key="processName">{field}</div>
            </UITooltip>
          </div>
        );
      },
    },
    {
      title: "Device Name",
      dataIndex: "deviceName",
      className:"activeProcessTable",
      key:"deviceName",
      width: "30%",
      ellipsis: true,
      // sorter: true,
      render: (field: any, row: any) => {
        return (
          <div  onClick={() => onRowClick(row)}>
            <UITooltip title={row?.deviceName} placement="top">
            <div className="tableData slice" key="deviceName">{field}</div>
            </UITooltip>
          </div>
        );
      },
    },
    {
      title: "Device Status",
      dataIndex: "deviceStatus",
      width: "30%",
      key:"deviceStatus",
      className:"activeProcessTable",
      ellipsis: true,
      // sorter: true,
      render: (field: any, row: any) => {
        return (
          <div onClick={() => onRowClick(row)}>
            <UITooltip title={row?.deviceStatus} placement="top">
            <div className="tableData slice" key="deviceStatus">{field}</div>
            </UITooltip>
          </div>
        );
      },
    },
    {
      title: "IP Address",
      dataIndex: "ipAddress",
      width: "30%",
      className:"activeProcessTable",
      key:"ipAddress",
      ellipsis: true,
      // sorter: true,
      render: (field: any, row: any) => {
        return (
          <div onClick={() => onRowClick(row)}>
             <UITooltip title={row?.ipAddress} placement="top">
            <div className="tableData slice" key="ipAddress">{field}</div>
            </UITooltip>
          </div>
        );
      },
    },
    {
      title: "Site",
      key:"siteName",
      dataIndex: "siteName",
      width: "22%",
      className:"activeProcessTable",
      ellipsis: true,
      render: (field: any, row: any) => {
        return (
          <div onClick={() => onRowClick(row)}>
            <UITooltip title={row?.siteName} placement="top">
            <div className="tableData slice" key="siteName">{field}</div>
            </UITooltip>
          </div>
        );
      },
    },
    {
      title: "Zone",
      dataIndex: "zoneName",
      key:"zoneName",
      width: "22%",
      className:"activeProcessTable",
      ellipsis: true,
      render: (field: any, row: any) => {
        return (
          <div onClick={() => onRowClick(row)}>
            <UITooltip title={row?.zoneName} placement="top">
            <div className="tableData slice" key="zoneName">{field}</div>
            </UITooltip>
          </div>
        );
      },
    },
    // {
    //   title: "Last Sync Time",
    //   dataIndex: "lastSyncTime",
    //   width: "30%",
    //   key:"lastSyncTime",
    //   className:"activeProcessTable",
    //   ellipsis: true,
    //   render: (field: any, row: any) => {
    //     return (
    //       <div onClick={() => onRowClick(row)}>
    //         <UITooltip title={moment(field).format("DD MMM YYYY hh:mm A")} placement="top">
    //           <div className="tableData slice" key="lastSyncTime">{moment(field).format("DD MMM YYYY hh:mm A")}</div>
    //         </UITooltip>
    //       </div>
    //     );
    //   }
    // },
    {
      title: "Process status",
      dataIndex: "isReadyForProcessing",
      width: "30%",
      key:"isReadyForProcessing",
      className:"activeProcessTable",
      ellipsis: true,
      render: (field: any, row: any) => {
        return (
          <div onClick={() => onRowClick(row)}>
            <UITooltip title={getStatus(field, row)} placement="top">
             <span className={getColorDot(field, row)}></span>
             <span className="tableData slice" key="isReadyForProcessing">{getStatus(field, row)}</span>
              </UITooltip>
          </div>
        );
      },
    },
    {
      title: "USERNAME",
      dataIndex: "userName",
      width: "25%",
      key:"userName",
      className:"activeProcessTable",
      ellipsis: true,
      render: (field: any, row: any) => {

        const displayValue = row?.username || row?.userName || field;
        return (
          <div onClick={() => onRowClick(row)}>
            <UITooltip title={displayValue} placement="top">
            <div  className="slice" key="userName">{displayValue}</div>
            </UITooltip>
          </div>
        );
      },
    },
    
    {
      title: "Actions",
      dataIndex: "login",
      width: "15%",
      key:"login",
      className:"activeProcessTable",
      fixed:"right",
      align: "center",
      render: (login: any, row: any) => {
        console.log('[ActiveProcess] Actions column render called for row:', {
          processId: row?.processId,
          processName: row?.processName,
          userName: row?.userName,
          userId: row?.userId,
          roleName: row?.roleName,
          roleId: row?.roleId
        });
        const dropdownResult = dropdown(row);
        console.log('[ActiveProcess] Dropdown result:', dropdownResult ? 'Component returned' : 'null returned');
        return dropdownResult;
      },
    },
  ];

  interface DataType {
    name: {
      first: string;
      last: string;
    };
    description: string;
    lastUpdated: string;
    createdAt: string;
  }
  interface Params {
    pagination?: TablePaginationConfig;
    sorter?: SorterResult<any> | SorterResult<any>[];
    total?: number;
    sortField?: string;
    sortOrder?: string;
  }

  useEffect(() => {
    setPagination({
      ...pagination,
      locale: { items_per_page: "" },
      total,
    });
  }, [total]);

  const fetchData = (params: Params = {}) => {
    getActiveProcess?.refetch()
    setSort({
      sortBy: params.sortField,
      sortOrder:
        params.sortOrder === "ascend"
          ? "1"
          : params.sortOrder === "descend"
          ? "-1"
          : undefined,
    });
    setPagination({
      ...params.pagination,
      locale: { items_per_page: "" },
      total,
    });
  };

  const handleTableChange = (
    newPagination: TablePaginationConfig,
    filters: Record<string, FilterValue>,
    sorter: SorterResult<DataType>
  ) => {
    fetchData({
      sortField: sorter.field as string,
      sortOrder: sorter.order as string,
      pagination: newPagination,
      ...filters,
    });
  };

    return (
      <>
        <section className="listing-section">
        <Card className="uicard table uiinner-card">
          <div className="card-body">
            <UITable
              columns={columns}
              data={tableData}
              rowKey="tenant"
              pagination={pagination}
              loading={loading}
              handleTableChange={handleTableChange}
              scroll={{ x: 1000, y: 500 }}
              rowSelection={onRowClick}
            />
          </div>
        </Card>
      </section>
      <UIConfirmModal
        data-testid="modal-id"
        key="deleteTenant"
        visible={deleteModal}
        cancelCallback={onDeleteModalClose}
        confirmCallback={handleDelete}
        cancelButton={ModalButton.CANCEL}
        confirmButton={ModalButton.CONFIRM}
        primaryText={util.format(ModalPrimaryText.DELETE, selectedRow.processName)}
        type={ModalType.WARN}
      />
      </>
    )
}